import { jsx as _jsx } from "react/jsx-runtime";
/**
 * 倒计时按钮组件
 * @param onSendCode 发送验证码回调，返回是否成功
 * @param disabled 是否禁用
 * @param className 自定义样式类
 * @param children 按钮内容（可选，默认使用多语言文本）
 * @param storageKey localStorage存储键，用于区分不同场景
 */
import { useState, useEffect, useCallback } from "react";
import styles from "./index.module.less";
import { useLocale } from "@/hooks/useLocale";
// 倒计时相关常量
const COUNTDOWN_DURATION = 59; // 倒计时总时长（秒）
/**
 * 倒计时按钮组件
 */
const CountdownButton = ({ onSendCode, disabled = false, className = "", children, storageKey = "default_countdown", }) => {
    const { t } = useLocale("accountManagement");
    // 倒计时状态
    const [countdown, setCountdown] = useState(0);
    // 是否正在倒计时
    const [isCountingDown, setIsCountingDown] = useState(false);
    // 从 localStorage 恢复倒计时状态
    useEffect(() => {
        const savedCountdown = localStorage.getItem(storageKey);
        if (savedCountdown) {
            const { endTime } = JSON.parse(savedCountdown);
            const now = Date.now();
            const remainingTime = Math.ceil((endTime - now) / 1000);
            if (remainingTime > 0) {
                setCountdown(remainingTime);
                setIsCountingDown(true);
            }
            else {
                // 倒计时已过期，清除存储
                localStorage.removeItem(storageKey);
            }
        }
    }, [storageKey]);
    // 倒计时逻辑
    useEffect(() => {
        let timer;
        if (isCountingDown && countdown > 0) {
            timer = setTimeout(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        // 倒计时结束
                        setIsCountingDown(false);
                        localStorage.removeItem(storageKey);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer)
                clearTimeout(timer);
        };
    }, [isCountingDown, countdown, storageKey]);
    // 获取验证码
    const handleGetVerificationCode = useCallback(async () => {
        // 防止重复点击
        if (isCountingDown || disabled) {
            return;
        }
        try {
            // 调用发送验证码的回调
            const success = await onSendCode();
            if (success) {
                // 开始倒计时
                setCountdown(COUNTDOWN_DURATION);
                setIsCountingDown(true);
                // 保存到 localStorage，包含结束时间
                const endTime = Date.now() + COUNTDOWN_DURATION * 1000;
                localStorage.setItem(storageKey, JSON.stringify({
                    endTime,
                }));
            }
        }
        catch (error) {
            console.error("发送验证码失败:", error);
        }
    }, [onSendCode, isCountingDown, disabled, storageKey]);
    // 计算按钮样式
    const buttonStyle = {
        cursor: isCountingDown || disabled ? "not-allowed" : "pointer",
    };
    return (_jsx("div", { className: `${className} ${isCountingDown ? styles.countingDown : ""}`, onClick: handleGetVerificationCode, style: buttonStyle, "data-counting-down": isCountingDown, "data-countdown": countdown, children: isCountingDown
            ? `${t("resendButton")}(${countdown})`
            : children || t("getCodeButton") }));
};
export default CountdownButton;
